/// <reference types="google.accounts" />

import { ComponentProps, createContext, JSX, onMount } from "solid-js";
import Cookies from "js-cookie";

import Compare from "@repo/utils/Compare";
import UFunction from "@repo/utils/UFunction";
import UHTMLScriptElement from "@repo/utils-client/UHTMLScriptElement";
import USolid from "@repo/utils-solid/USolid";

namespace GoogleSignIn {
  export type Callback = NonNullable<
    google.accounts.id.IdConfiguration["callback"]
  >;
  const callbacks = new Set<Callback>();

  export namespace Callback {
    export function add(callback: Callback) {
      callbacks.add(callback);
      return {
        remove() {
          callbacks.delete(callback);
        },
      };
    }
    export function removeAll() {
      callbacks.clear();
    }
  }

  export const load = UFunction.memo((clientId: string, apiDomain: string) =>
    UHTMLScriptElement.inject("https://accounts.google.com/gsi/client").then(
      () => {
        if (!google.accounts.id)
          throw new Error("Google Sign In failed to load.");

        const id = crypto.randomUUID();
        const nonce = crypto.randomUUID();

        // Only allow up to 20 CSRF tokens to be stored at a time, will
        // otherwise inflate header size. The only reason more than 20 would be
        // necessary are if there are more than 20 sessions going at once
        {
          const gsiCookies = document.cookie
            .split(";")
            .map((c) => c.trim())
            .filter((c) => c.startsWith("gsi_csrf_token"))
            .map((c) => {
              const [name, value] = c.split("=");
              const [timestamp] = value!.split(":");
              return { name, timestamp: parseInt(timestamp) };
            })
            .sort(Compare.comparator([[({ timestamp }) => timestamp, "desc"]]));

          // Keep only the 20 most recent cookies, delete the rest
          const cookiesToDelete = gsiCookies.slice(20);
          cookiesToDelete.forEach(({ name }) => {
            Cookies.remove(name, { domain: apiDomain });
          });
        }

        Cookies.set(`gsi_csrf_token-${id}`, `${Date.now()}:${nonce}`, {
          domain: apiDomain,
          sameSite: "Lax",
          secure: true,
          session: true,
        });

        google.accounts.id.initialize({
          client_id: clientId,
          callback(response) {
            for (const callback of callbacks) {
              callback(response);
            }
          },
          nonce: `${id}:${nonce}`,
        });
        return google.accounts.id;
      },
    ),
  );

  export declare namespace Button {
    type Props = {
      ref?: ComponentProps<"div">["ref"];
      class?: string;
    };
  }

  export function Button({ ref: _ref, class: className }: D<Button.Props>) {
    const client = useContext();

    let ref: HTMLDivElement = undefined!;

    onMount(async () => {
      const isLight = document.documentElement.dataset.theme === "light";

      (await client.load()).renderButton(ref, {
        type: "standard",
        theme: isLight ? "outline" : "filled_black",
        size: "large",
        shape: "pill",
        text: "continue_with",
      });
    });

    return (
      <div
        ref={(el) => {
          ref = el;
          if (typeof _ref === "function") _ref(el);
        }}
        class={className}
      />
    );
  }

  const Context = createContext<
    Omit<Provider.Props, "children" | "apiDomain"> & {
      load(): Promise<typeof google.accounts.id>;
    }
  >();
  export declare namespace Provider {
    type Props = {
      clientId: string;
      apiDomain: string;
      children?: JSX.Element;
    };
  }
  export function Provider({
    clientId,
    apiDomain,
    children,
  }: D<Provider.Props>) {
    return (
      <Context.Provider
        value={{ load: () => load(clientId, apiDomain), clientId }}
      >
        {children}
      </Context.Provider>
    );
  }
  export function useContext() {
    const context = USolid.useContext(Context);

    if (!context)
      throw new Error(
        "Cannot call GoogleSso.useContext outside of GoogleSso.Provider.",
      );

    return context;
  }
}

export default GoogleSignIn;
